import React, { FC } from "react";
import type { ContentfulContactModalType } from "@/types";
import { ContentManager } from "./content-manager";
import { FormCheck, FormSelect, FormText } from "./form";
import { Link } from "./link";
import { countries } from "@/data/countries";
import { FormRadio } from "./form/radio";

type FormInput = {
  value: string | boolean;
  errorMessage?: string;
  fieldName: string;
};

type FormState = {
  [key: string]: FormInput;
};

export const ContactForm: FC<{
  onSubmit: (event: React.FormEvent) => void;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  isModal?: boolean;
  className?: string;
}> = ({ onSubmit, form, setForm, isModal, className }) => {
  return (
    <form className={`contactForm ${className || ""}`} onSubmit={onSubmit}>
      <div className="contactForm--title">Write a Message</div>
      <div className="contactForm--fields">
        <FormText
          label="Full Name"
          value={form.name.value as string}
          required
          type="text"
          inputAttrs={{
            id: "input_7",
            name: "q7_fullName",
          }}
          errorMessage={form.name.errorMessage}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              name: {
                value,
                errorMessage: "",
                fieldName: "q7_fullName",
              },
            }));
          }}
        />
        <FormText
          label="Company Email"
          value={form.email.value as string}
          required
          type="email"
          inputAttrs={{
            id: "input_4",
            name: "q4_email",
          }}
          errorMessage={form.email.errorMessage}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              email: {
                value,
                errorMessage: "",
                fieldName: "q4_email",
              },
            }));
          }}
        />
        <FormText
          label="Phone Number"
          value={form.phone.value as string}
          type="tel"
          inputAttrs={{
            id: "input_5_full",
            name: "q5_phoneNumber[full]",
          }}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              phone: {
                value,
                errorMessage: "",
                fieldName: "q5_phoneNumber[full]",
              },
            }));
          }}
        />
        {!isModal && (
          <FormText
            label="Company Name"
            value={form.companyName.value as string}
            required
            type="text"
            inputAttrs={{
              id: "input_8",
              name: "q8_company",
            }}
            errorMessage={form.companyName.errorMessage}
            onChange={(value) => {
              setForm((form) => ({
                ...form,
                companyName: {
                  value,
                  errorMessage: "",
                  fieldName: "q8_company",
                },
              }));
            }}
          />
        )}
        <FormText
          label="Company Website"
          value={form.website.value as string}
          required
          type="text"
          inputAttrs={{
            id: "input_9",
            name: "q9_companyWebsite",
          }}
          errorMessage={form.website.errorMessage}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              website: {
                value,
                errorMessage: "",
                fieldName: "q9_companyWebsite",
              },
            }));
          }}
        />
        <FormText
          label="Job Title"
          value={form.jobTitle.value as string}
          required
          type="text"
          inputAttrs={{
            id: "input_10",
            name: "q10_jobTitle",
          }}
          errorMessage={form.jobTitle.errorMessage}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              jobTitle: {
                value,
                errorMessage: "",
                fieldName: "q10_jobTitle",
              },
            }));
          }}
        />
        <FormSelect
          label="Country"
          value={form.country.value as string}
          options={countries}
          errorMessage={form.country.errorMessage}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              country: {
                value,
                errorMessage: "",
                fieldName: "q12_country",
              },
            }));
          }}
        />
        <div className="contactForm--checks">
          <FormRadio
            label="Regulated Financial Institution"
            name="type"
            selected={form.type.value as string}
            value="Regulated Financial Institution"
            inputAttrs={{
              id: "input_6_0",
              name: "q6_typeA",
            }}
            errorMessage={form.type.errorMessage}
            onChange={(value) => {
              setForm((form) => ({
                ...form,
                type: {
                  value,
                  errorMessage: "",
                  fieldName: "q6_typeA",
                },
              }));
            }}
          />
          <FormRadio
            label="Commercial Entity"
            name="type"
            selected={form.type.value as string}
            value="Commercial Entity"
            inputAttrs={{
              id: "input_6_1",
              name: "q6_typeA",
            }}
            errorMessage={form.type.errorMessage}
            onChange={(value) => {
              setForm((form) => ({
                ...form,
                type: {
                  value,
                  errorMessage: "",
                  fieldName: "q6_typeA",
                },
              }));
            }}
          />
        </div>
        <FormText
          label="I'm interested in..."
          value={form.interestedIn.value as string}
          required
          type="textarea"
          inputAttrs={{
            id: "input_13",
            name: "q13_imInterested",
          }}
          errorMessage={form.interestedIn.errorMessage}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              interestedIn: {
                value,
                errorMessage: "",
                fieldName: "q13_imInterested",
              },
            }));
          }}
        />
        {isModal && (
          <FormCheck
            label="I agree to receive other communications from Payall."
            value={form.receiveOtherCommunications.value as boolean}
            errorMessage={form.receiveOtherCommunications.errorMessage}
            onChange={(value) => {
              setForm((form) => ({
                ...form,
                receiveOtherCommunications: {
                  value,
                  errorMessage: "",
                  fieldName: "q14_iAgree",
                },
              }));
            }}
          />
        )}
      </div>
      <div className="contactForm--bottom">
        {!isModal && (
          <FormCheck
            label="I agree to receive other communications from Payall."
            value={form.receiveOtherCommunications.value as boolean}
            errorMessage={form.receiveOtherCommunications.errorMessage}
            onChange={(value) => {
              setForm((form) => ({
                ...form,
                receiveOtherCommunications: {
                  value,
                  errorMessage: "",
                  fieldName: "q14_iAgree",
                },
              }));
            }}
          />
        )}
        <button
          type="submit"
          className="link link_full link_blue contactForm--submit"
          data-title="Submit"
        >
          <span>Submit</span>
        </button>
        <div className="contactForm--footer">
          Payall is committed to protecting and respecting your privacy, and we’ll 
          only use your personal information to administer your account and to provide 
          the products and services you requested from us.<br/><br/>
          From time to time, we would like to contact you about our products and services, 
          as well as deliver other content that may be of interest to you. If you consent 
          to us contacting you for this purpose, please click the “I agree to receive other 
          communications from Payall.” box. You may unsubscribe from these communications at 
          any time. For more information on how to unsubscribe, our privacy practices and 
          how we are committed to protecting and respecting your privacy, please review 
          our {" "}<Link href="/privacy-policy" noStyle>Privacy Policy.</Link><br/><br/>
          By clicking “Submit,” you consent to allow Payall to store and process the 
          personal information submitted above to provide you with the content requested.
        </div>
      </div>
    </form>
  );
};

export const ContactModal: FC<
  ContentfulContactModalType & {
    onClose: () => void;
  }
> = ({ title, subtitle, content, onClose }) => {
  const [formSuccess, setFormSuccess] = React.useState<boolean | null>(null);

  const [form, setForm] = React.useState<{
    [key: string]: FormInput;
  }>({
    name: {
      value: "",
      errorMessage: "",
      fieldName: "q7_fullName",
    },
    email: {
      value: "",
      errorMessage: "",
      fieldName: "q4_email",
    },
    phone: {
      value: "",
      fieldName: "q5_phoneNumber[full]",
    },
    website: {
      value: "",
      errorMessage: "",
      fieldName: "q9_companyWebsite",
    },
    jobTitle: {
      value: "",
      errorMessage: "",
      fieldName: "q10_jobTitle",
    },
    country: {
      value: "",
      errorMessage: "",
      fieldName: "q12_country",
    },
    type: {
      value: "",
      errorMessage: "",
      fieldName: "q6_typeA",
    },
    interestedIn: {
      value: "",
      errorMessage: "",
      fieldName: "q13_imInterested",
    },
    receiveOtherCommunications: {
      value: false,
      errorMessage: "",
      fieldName: "q14_iAgree",
    },
  });

  const resetFormValues = () => {
    Object.keys(form).forEach((key) => {
      setForm((form) => ({
        ...form,
        [key]: {
          ...form[key],
          value: "",
        },
      }));
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const requiredFields = [
      "name",
      "email",
      "website",
      "jobTitle",
      "interestedIn",
      "receiveOtherCommunications",
    ];

    const invalidFields = requiredFields.filter((field) => {
      if (!form[field].value) {
        setForm((form) => ({
          ...form,
          [field]: {
            ...form[field],
            errorMessage: "This field is required",
          },
        }));
        return true;
      }
      return false;
    });

    // if (
    //   form.website.value &&
    //   !(form.website.value as string).toLowerCase().startsWith("http") &&
    //   !(form.website.value as string).toLowerCase().startsWith("www")
    //   // !(form.website.value as string).startsWith("http") &&
    //   // !(form.website.value as string).startsWith("www")
    // ) {
    //   setForm((form) => ({
    //     ...form,
    //     website: {
    //       ...form.website,
    //       errorMessage: "Please enter a valid URL",
    //     },
    //   }));
    //   invalidFields.push("website");
    // }

    if (invalidFields.length > 0) {
      return;
    } else {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        formData.append(form[key].fieldName, JSON.stringify(form[key].value).replace(/"/g, ''));
      });
      try {
        const response = await fetch(
          "https://eu-submit.jotform.com/submit/231794305773361",
          {
            method: "POST",
            body: formData,
          }
        );

        if (response.ok) {
          // Handle successful submission
          console.log("Form submitted successfully");
          setFormSuccess(true);
          resetFormValues();
        } else {
          setFormSuccess(false);
          console.error("Form submission failed");
        }
      } catch (error) {
        setFormSuccess(false);
        console.error("An error occurred during form submission:", error);
      } finally {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <section className="contactModal">
      <div className="contactModal--inner">
        <div className="contactModal--top">
          <div className="contactModal--title">{title}</div>
          <button className="contactModal--close" onClick={onClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="contactModal--main">
          <div className="contactModal--text">
            <div className="contactModal--subtitle">{subtitle}</div>
            <ContentManager
              content={content}
              className="contactModal--content"
            />
          </div>
          {formSuccess === null && (
            <ContactForm
              onSubmit={handleSubmit}
              form={form}
              setForm={setForm}
              isModal
              className="contactModal--form"
            />
          )}
          {formSuccess === true && (
            <div className="contactModal--success">
              Thank You!
              <br />
              <br />
              Your submission has been received.
            </div>
          )}
          {formSuccess === false && (
            <div className="contactModal--error">
              There was an error, please try again later.
            </div>
          )}
        </div>
        <div className="contactModal--footer">
          <button onClick={onClose} className="link link_outline link_blue">
            Cancel
          </button>
          {formSuccess === null && (
            <button onClick={handleSubmit} className="link link_full link_blue">
              Submit
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
